import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

type ServiceStatus = { operable: boolean; checkedAt: number };

const SERVICE_CACHE_TTL = 60_000;
const serviceStatusCache = new Map<string, ServiceStatus>();
const serviceStatusRequests = new Map<string, Promise<boolean>>();

const fetchServiceStatus = (key: string, gimnasioId: string | null) => {
  const existingRequest = serviceStatusRequests.get(key);
  if (existingRequest) return existingRequest;

  const request = (async () => {
    try {
      const { data, error } = await supabase.rpc('gym_service_snapshot', { target_gym_id: gimnasioId });
      if (error) {
        console.error('Error validando servicio SaaS:', error);
        return true;
      }

      const row = Array.isArray(data) && data.length > 0 ? data[0] : null;
      return Boolean(row?.operable);
    } finally {
      serviceStatusRequests.delete(key);
    }
  })();

  serviceStatusRequests.set(key, request);
  return request;
};

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

export default function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { loading, profile } = useAuth();
  const location = useLocation();
  const serviceKey = profile && profile.role !== 'platform_admin'
    ? `${profile.role}:${profile.gimnasioId || 'sin-gimnasio'}`
    : null;
  const cachedStatus = serviceKey ? serviceStatusCache.get(serviceKey) : null;
  const [checkingService, setCheckingService] = useState(() => Boolean(serviceKey && !cachedStatus));
  const [serviceOperable, setServiceOperable] = useState(() => cachedStatus?.operable ?? true);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      if (!profile || profile.role === 'platform_admin') {
        setServiceOperable(true);
        setCheckingService(false);
        return;
      }

      const key = `${profile.role}:${profile.gimnasioId || 'sin-gimnasio'}`;
      const cached = serviceStatusCache.get(key);
      if (cached) {
        setServiceOperable(cached.operable);
        setCheckingService(false);
        if (Date.now() - cached.checkedAt < SERVICE_CACHE_TTL) return;
      } else {
        setCheckingService(true);
      }

      const operable = await fetchServiceStatus(key, profile.gimnasioId || null);
      serviceStatusCache.set(key, { operable, checkedAt: Date.now() });
      if (!cancelled) {
        setServiceOperable(operable);
        setCheckingService(false);
      }
    };

    void check();
    return () => {
      cancelled = true;
    };
  }, [profile]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (!profile) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && allowedRoles.length > 0 && !allowedRoles.includes(profile.role)) {
    return <Navigate to="/" replace />;
  }

  if (
    profile.role === 'staff' &&
    ['coach_interno', 'coach_externo'].includes(profile.tipoStaff) &&
    ![
      '/progreso',
      '/horarios',
      '/chat',
      '/actividades-coaches',
      '/invitaciones-coach',
      '/pagar-servicio/return',
      ...(profile.tipoStaff === 'coach_interno' ? ['/rutinas', '/reportes'] : []),
    ].includes(location.pathname)
  ) {
    return <Navigate to="/progreso" replace />;
  }

  if (
    profile.role === 'staff' &&
    profile.tipoStaff === 'recepcionista' &&
    !['/', '/usuarios', '/reservas', '/invitados', '/invitaciones-coach', '/presencia-personal'].includes(location.pathname)
  ) {
    return <Navigate to="/" replace />;
  }

  if (
    profile.role === 'staff' &&
    profile.tipoStaff === 'nutricionista' &&
    !['/', '/progreso'].includes(location.pathname)
  ) {
    return <Navigate to="/progreso" replace />;
  }

  if (checkingService) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (
    profile.role !== 'platform_admin' &&
    !serviceOperable &&
    !location.pathname.startsWith('/pagar-servicio')
  ) {
    return <Navigate to="/pagar-servicio" replace />;
  }

  return <>{children}</>;
}
